import { useMemo } from 'react';
import { useField } from 'formik';
import { Grid, Alert, Divider } from '@mui/material';

import { FormFieldType } from 'types';
import { InputField, SelectField } from 'components';
import { outcomeFieldsDisallowedNestedFieldTypes } from 'utils/form';

import { Fields } from './fields';
import { FormOutcomeFieldsFieldProps } from './types';
import { EMPTY_OUTCOME_FIELD_SOURCES } from './config';

export const FormOutcomeFieldsField = ({
  name,
  value,
  simplified = false,
  allowReferenceCodes = false,
  outcomeFieldSources = EMPTY_OUTCOME_FIELD_SOURCES,
}: FormOutcomeFieldsFieldProps) => {
  const [, , { setValue }] = useField(name);
  const sourceOptions = useMemo(
    () =>
      outcomeFieldSources.map((source) => ({
        value: `${source.stepId}:${source.fieldId}`,
        label: `${source.stepName} / ${source.fieldLabel}`,
      })),
    [outcomeFieldSources]
  );
  const selectedSourceValue = useMemo(
    () =>
      value.options?.sourceCourseStepId && value.options?.sourceFieldId
        ? `${value.options.sourceCourseStepId}:${value.options.sourceFieldId}`
        : '',
    [value.options?.sourceCourseStepId, value.options?.sourceFieldId]
  );

  if (simplified) {
    return (
      <Fields
        simplified
        enableSorting
        name={`${name}.options.fields`}
        fields={value.options?.fields ?? []}
        allowReferenceCodes={allowReferenceCodes}
        allowOutcomes={
          !outcomeFieldsDisallowedNestedFieldTypes.has(FormFieldType.OUTCOMES)
        }
        allowPageBreak={
          !outcomeFieldsDisallowedNestedFieldTypes.has(FormFieldType.PAGE_BREAK)
        }
        allowFieldArray={
          !outcomeFieldsDisallowedNestedFieldTypes.has(
            FormFieldType.FIELD_ARRAY
          )
        }
        allowOutcomeFields={
          !outcomeFieldsDisallowedNestedFieldTypes.has(
            FormFieldType.OUTCOME_FIELDS
          )
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Divider />

      <Grid container spacing={2}>
        <Grid size={{ md: 9, xs: 12 }}>
          <SelectField
            fullWidth
            displayEmpty
            options={sourceOptions}
            value={selectedSourceValue}
            label="Source outcomes field"
            name={`${name}.options.sourceFieldId`}
            onChange={(event) => {
              const selected = outcomeFieldSources.find(
                (item) =>
                  `${item.stepId}:${item.fieldId}` === event.target.value
              );

              setValue({
                ...value,
                options: {
                  ...value.options,
                  sourceFieldId: selected?.fieldId ?? '',
                  sourceCourseStepId: selected?.stepId ?? '',
                },
              });
            }}
          />
        </Grid>

        <Grid size={{ md: 3, xs: 12 }}>
          <InputField
            fullWidth
            type="number"
            label="Max outcomes"
            placeholder="Optional"
            name={`${name}.options.maxOutcomeCount`}
          />
        </Grid>
      </Grid>

      <Alert severity="info">
        These fields will repeat for each resolved outcome label from the
        selected source.
      </Alert>

      <Fields
        name={`${name}.options.fields`}
        fields={value.options?.fields ?? []}
        allowReferenceCodes={allowReferenceCodes}
        allowOutcomes={
          !outcomeFieldsDisallowedNestedFieldTypes.has(FormFieldType.OUTCOMES)
        }
        allowPageBreak={
          !outcomeFieldsDisallowedNestedFieldTypes.has(FormFieldType.PAGE_BREAK)
        }
        allowFieldArray={
          !outcomeFieldsDisallowedNestedFieldTypes.has(
            FormFieldType.FIELD_ARRAY
          )
        }
        allowOutcomeFields={
          !outcomeFieldsDisallowedNestedFieldTypes.has(
            FormFieldType.OUTCOME_FIELDS
          )
        }
      />
    </div>
  );
};
